import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, ParamMap, Router } from '@angular/router';
import { environment } from '../../environments/environment';

/**
 * Whether the registration pause applies to the visitor in front of us.
 *
 * The team still has to exercise the real production flow while the public is held
 * out, so a visit carrying `?preview=<anything>` goes through and is remembered for
 * the rest of the browser tab. This is a curtain, not a security boundary: it only
 * keeps ordinary visitors from registering, and guards nothing that matters if the
 * value leaks.
 */
export const MAINTENANCE_BYPASS_PARAM = 'preview';
export const MAINTENANCE_BYPASS_STORAGE_KEY = 'QUANSIC_MAINTENANCE_BYPASS';

const rememberBypass = (): void => {
    // Storage is unavailable in some privacy modes; the bypass then lasts for the
    // single navigation carrying the query param, which is still usable.
    try { sessionStorage.setItem(MAINTENANCE_BYPASS_STORAGE_KEY, "1"); } catch (e) {}
}

const bypassRemembered = (): boolean => {
    try { return sessionStorage.getItem(MAINTENANCE_BYPASS_STORAGE_KEY) === "1"; } catch (e) { return false; }
}

/**
 * Records the bypass from the URL the browser actually loaded, before the router runs.
 *
 * The root path redirects to /app-home, and reading the parameter from a route snapshot
 * on the other side of that redirect depends on how the router carries query strings
 * across it. The address bar is the one place the parameter is guaranteed to be, so
 * that is where it is read — once, at bootstrap.
 */
export const captureMaintenanceBypass = (): void => {
    try {
        if(new URLSearchParams(window.location.search).has(MAINTENANCE_BYPASS_PARAM)) rememberBypass();
    } catch (e) {}
}

/**
 * Pass the current route's query params so an in-app navigation carrying ?preview is
 * picked up too; otherwise this falls back to what the initial visit remembered.
 */
export const maintenanceActive = (queryParams?: ParamMap): boolean => {
    if(!environment.maintenance.enabled) return false;
    if(queryParams?.has(MAINTENANCE_BYPASS_PARAM)) {
        rememberBypass();
        return false;
    }
    return !bypassRemembered();
}

/** Sends the registration routes to the maintenance page while the pause is on. */
export const maintenanceGuard: CanActivateFn = (route: ActivatedRouteSnapshot) =>
    maintenanceActive(route.queryParamMap) ? inject(Router).createUrlTree(['/maintenance']) : true;
